(async function () {
  const BONUS_IDS = [];

  const MIN_PAID_ITEMS_FOR_GIFT = 3;
  const MIN_SPEND_FOR_GIFT = 0;

  // Quando true: se o usuário remover manualmente um brinde, ele NÃO será re-adicionado.
  // O estado é persistido em cart attributes (_removed_gift_<id> = "true").
  const ALLOW_REMOVE_GIFT = false;

  const REMOVED_GIFT_ATTR_PREFIX = '_removed_gift_';
  const DEBOUNCE_MS = 300;

  // Shopify's native Bundles app doesn't expose component composition via /cart.js —
  // a bundle line item looks identical to a normal single-SKU product (quantity: 1,
  // one product_id, one sku). So we have to manually tell bonus.js how many "paid
  // items" each bundle product should count as, per bundle unit purchased.
  const BUNDLE_ITEM_WEIGHTS = {
    8498727583930: 4, // Kate's Vitality Bundle
  };

  let syncing = false;
  let debounceTimer = null;
  let lastCartState = null;
  let lastGiftIds = null; // Set com os IDs de brinde presentes no último sync

  // Generates simple hash from the cart to detect real time changes
  function getCartHash(cart) {
    const items = cart.items.map(i => `${i.id}:${i.quantity}:${i.properties?.gift_item || ''}`).sort().join('|');
    return items;
  }

  // --- Helpers de Cart ---
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to load cart');
    return await res.json();
  }

  // Uma linha é "brinde" se tiver a property, independente de o ID ainda
  // pertencer à promo atual. Isso evita que um brinde de uma promo anterior
  // (cujo ID saiu de BONUS_IDS) passe a ser contado como item pago.
  function isGiftLine(item) {
    return item.properties?.gift_item === 'true';
  }

  function isCurrentPromoGiftLine(item) {
    return BONUS_IDS.includes(item.id) && isGiftLine(item);
  }

  function getLineItemWeight(item) {
    const perUnit = BUNDLE_ITEM_WEIGHTS[item.product_id] || 1;
    return perUnit * item.quantity;
  }

  function countPaidItems(cart) {
    return cart.items.reduce((sum, it) => sum + (isGiftLine(it) ? 0 : getLineItemWeight(it)), 0);
  }

  function calcPaidSpend(cart) {
    return cart.items.reduce((sum, it) => {
      if (isGiftLine(it)) return sum;
      return sum + it.final_line_price; // already in cents, excludes discounts
    }, 0);
  }

  // Todas as linhas de brinde, de qualquer promo (inclusive antigas).
  function getGiftLinesStrict(cart) {
    return cart.items.filter(isGiftLine);
  }

  // Só as linhas de brinde da promo atual (IDs presentes em BONUS_IDS).
  function getCurrentPromoGiftLines(cart) {
    return cart.items.filter(isCurrentPromoGiftLine);
  }

  // Brindes de promos antigas: têm a property mas o ID não está mais em BONUS_IDS.
  function getStaleGiftLines(cart) {
    return cart.items.filter((it) => isGiftLine(it) && !BONUS_IDS.includes(it.id));
  }

  // TEMPORARY: these bundle products always qualify for the free gift, regardless
  // of the paid-item/spend thresholds below. This is a stopgap until bonus.js can
  // read real bundle-component data from the Storefront API (see BUNDLE_ITEM_WEIGHTS
  // above for the interim per-item-count workaround). Remove once that's in place.
  const TEMP_ALWAYS_GIFT_PRODUCT_IDS = [
    8498727583930, // Kate's Vitality Bundle
  ];

  function meetsGiftConditions(cart) {
    const hasTempGiftProduct = cart.items.some(
      (it) => !isGiftLine(it) && TEMP_ALWAYS_GIFT_PRODUCT_IDS.includes(it.product_id)
    );
    if (hasTempGiftProduct) return true;

    const paidCount = countPaidItems(cart);
    const paidSpend = calcPaidSpend(cart);
    return (
      (MIN_PAID_ITEMS_FOR_GIFT && paidCount >= MIN_PAID_ITEMS_FOR_GIFT) ||
      (MIN_SPEND_FOR_GIFT && paidSpend >= MIN_SPEND_FOR_GIFT)
    );
  }

  // --- Helpers de remoção manual via cart attributes ---
  function getManuallyRemovedIds(cart) {
    const removed = new Set();
    const attrs = cart.attributes || {};
    for (const [key, value] of Object.entries(attrs)) {
      if (key.startsWith(REMOVED_GIFT_ATTR_PREFIX) && String(value) === 'true') {
        const id = parseInt(key.slice(REMOVED_GIFT_ATTR_PREFIX.length), 10);
        if (!Number.isNaN(id)) removed.add(id);
      }
    }
    return removed;
  }

  async function updateCartAttributes(attributes) {
    if (!attributes || !Object.keys(attributes).length) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });
  }

  async function markGiftsAsManuallyRemoved(ids) {
    if (!ids.length) return;
    console.log('🚫 Marking gifts as manually removed:', ids);
    const attributes = {};
    for (const id of ids) attributes[`${REMOVED_GIFT_ATTR_PREFIX}${id}`] = 'true';
    await updateCartAttributes(attributes);
  }

  async function clearManuallyRemovedFlags(cart) {
    const attrs = cart.attributes || {};
    const toClear = {};
    for (const key of Object.keys(attrs)) {
      if (key.startsWith(REMOVED_GIFT_ATTR_PREFIX)) {
        toClear[key] = ''; // Shopify remove o attribute quando o valor é string vazia
      }
    }
    if (!Object.keys(toClear).length) return false;
    console.log('🧹 Clearing manually-removed flags:', Object.keys(toClear));
    await updateCartAttributes(toClear);
    return true;
  }

  async function addGifts(missingBonusIds) {
    if (!missingBonusIds.length) return false;

    console.log('🎁 Adding gifts:', missingBonusIds);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: missingBonusIds.map((id) => ({
          id,
          quantity: 1,
          properties: { gift_item: 'true' },
        })),
      }),
    });

    if (!res.ok) throw new Error('Failed to add gifts');
    return true;
  }

  async function removeAllStrictGifts(cart) {
    const strict = getGiftLinesStrict(cart);
    if (!strict.length) return false;

    console.log('🗑️ Removing gifts:', strict.map(l => l.key));

    const updates = {};
    for (const line of strict) updates[line.key] = 0;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  // Remove brindes deixados por promos anteriores (property gift_item="true"
  // mas o ID não está mais em BONUS_IDS). Sem isso, esses itens ficam presos
  // no carrinho para sempre e passam a ser contados como itens pagos.
  async function removeStaleGifts(cart) {
    const stale = getStaleGiftLines(cart);
    if (!stale.length) return false;

    console.log('🗑️ Removing stale gifts from previous promo:', stale.map(l => l.key));

    const updates = {};
    for (const line of stale) updates[line.key] = 0;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  async function splitExcessGiftQuantityIntoPaid(cart) {
    const strict = getCurrentPromoGiftLines(cart);
    const tasks = [];

    for (const line of strict) {
      if (line.quantity > 1) {
        console.log('✂️ Splitting excess gift qty:', line.quantity);
        const extraQty = line.quantity - 1;

        tasks.push(
          Promise.all([
            fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ id: line.key, quantity: 1 }),
            }),
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ items: [{ id: line.id, quantity: extraQty }] }),
            }),
          ])
        );
      }
    }

    if (!tasks.length) return false;
    await Promise.all(tasks);
    return true;
  }

  async function normalizeGiftQuantities(cart) {
    const dirty = getCurrentPromoGiftLines(cart).filter((l) => l.quantity !== 1);
    if (!dirty.length) return false;

    console.log('🔧 Normalizing gift quantities');

    const updates = {};
    for (const l of dirty) updates[l.key] = 1;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  async function syncBonusNow() {
    if (syncing) {
      console.log('⏳ Already syncing, skipping');
      return;
    }

    syncing = true;
    console.log('🔄 Sync started');

    try {
      const cart = await fetchCart();
      const currentHash = getCartHash(cart);

      if (currentHash === lastCartState) {
        console.log('✅ Cart unchanged, skipping');
        return;
      }

      lastCartState = currentHash;

      const paidCount = countPaidItems(cart);
      const paidSpend = calcPaidSpend(cart);
      const currentGiftIds = new Set(getGiftLinesStrict(cart).map((l) => l.id));

      console.log('📊 Paid items:', paidCount);
      console.log('📊 Paid spend:', paidSpend);

      let mutated = false;
      const conditionsMet = meetsGiftConditions(cart);

      // --- Detecção de remoção manual ---
      // Só faz sentido detectar remoção quando:
      //   1) ALLOW_REMOVE_GIFT está ativo;
      //   2) já temos um snapshot anterior (lastGiftIds);
      //   3) as condições para ter brinde ainda são atendidas
      //      (caso contrário, fomos NÓS que removemos, não o usuário).
      let removedByUser = [];
      if (ALLOW_REMOVE_GIFT && lastGiftIds && conditionsMet) {
        removedByUser = [...lastGiftIds].filter((id) => !currentGiftIds.has(id));
        if (removedByUser.length) {
          await markGiftsAsManuallyRemoved(removedByUser);
        }
      }

      if (conditionsMet) {
        // Filtra os brindes que o usuário removeu manualmente (se a feature estiver ligada).
        // IMPORTANTE: incluímos `removedByUser` aqui também, porque o objeto `cart` em
        // memória ainda não reflete os attributes que acabamos de gravar via API.
        // Sem isso, o brinde recém-removido seria re-adicionado nesta mesma sync.
        const blockedIds = ALLOW_REMOVE_GIFT ? getManuallyRemovedIds(cart) : new Set();
        for (const id of removedByUser) blockedIds.add(id);

        const presentStrictIds = new Set(getCurrentPromoGiftLines(cart).map((l) => l.id));
        const missingIds = BONUS_IDS.filter(
          (id) => !presentStrictIds.has(id) && !blockedIds.has(id)
        );

        if (missingIds.length) {
          await addGifts(missingIds);
          mutated = true;
        }

        // Brindes de uma promo anterior (ID não está mais em BONUS_IDS) nunca
        // devem ficar contando como item pago — removemos independentemente
        // do estado atual do carrinho atender ou não às condições da promo.
        const mutatedStale = await removeStaleGifts(cart);
        mutated = mutated || mutatedStale;

        const mutatedSplit = await splitExcessGiftQuantityIntoPaid(cart);
        mutated = mutated || mutatedSplit;

        if (mutatedSplit) {
          await new Promise(resolve => setTimeout(resolve, 200));
          const cart2 = await fetchCart();
          const mutatedNorm = await normalizeGiftQuantities(cart2);
          mutated = mutated || mutatedNorm;
        }
      } else {
        // Carrinho saiu do mínimo: removemos os brindes E limpamos os flags de
        // remoção manual, para que numa próxima compra o brinde possa voltar.
        mutated = await removeAllStrictGifts(cart);
        if (ALLOW_REMOVE_GIFT) {
          const cleared = await clearManuallyRemovedFlags(cart);
          mutated = mutated || cleared;
        }
      }

      if (mutated) {
        lastCartState = null;
        // Atualiza o snapshot de brindes a partir do estado real pós-mutação
        try {
          const finalCart = await fetchCart();
          lastGiftIds = new Set(getGiftLinesStrict(finalCart).map((l) => l.id));
        } catch (_) {
          lastGiftIds = currentGiftIds;
        }

        if (window.CartJS && typeof CartJS.getCart === 'function') {
          await CartJS.getCart();
          reloadSection('v3-cart');
        }
      } else {
        lastGiftIds = currentGiftIds;
      }

      console.log('✅ Sync completed, mutated:', mutated);
    } catch (err) {
      console.error('❌ Sync error:', err);
      lastCartState = null;
    } finally {
      syncing = false;
    }
  }

  function syncBonusDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncBonusNow, DEBOUNCE_MS);
  }

  // Prevents multiple bindings
  $(document).off('cart.requestComplete', syncBonusDebounced);
  $(document).on('cart.requestComplete', syncBonusDebounced);

  // Inicial
  setTimeout(syncBonusNow, 300);
})();